import PlpPageTemplate from '@/components/PlpPageTemplate'
import ErrorPage from '@/components/ErrorPage'
import ProductDetailsPage from '@/components/ProductDetailsPage'
import { getCataloguePart2Response } from '@/helpers/TemplateHelper'
import { createCanonicalLink, getAgentConfiguration, isLanguageValid } from '@/helpers/UrlHelper'
import { PdpResponse, PlpResponse } from '@/types'
import { logger } from '@/logging/Logger'
import { withSessionSsr } from '@/config/withSession'
import { GetServerSidePropsContext } from 'next'
import { AppliedCertificate } from '@big-red-group/storefront-common-checkout'

const log = logger()

type Part2PageProps = {
  catalogueResponse: PlpResponse | PdpResponse
  canonicalLink: string
}

export default function Part2Page({ catalogueResponse, canonicalLink }: Part2PageProps) {
  if (catalogueResponse.error) {
    return <ErrorPage />
  }

  const { entityType } = catalogueResponse
  if (entityType === 'CATEGORY') {
    const { agentConfiguration, entityDetails, entityType, templateDefinition, loyaltyCampaign } = catalogueResponse

    return (
      <PlpPageTemplate
        agentConfiguration={agentConfiguration}
        entityDetails={entityDetails}
        entityType={entityType}
        templateDefinition={templateDefinition}
        loyaltyCampaign={loyaltyCampaign}
        canonicalLink={canonicalLink}
        key={entityDetails.id}
      />
    )
  }

  if (entityType === 'PRODUCT') {
    return (
      <ProductDetailsPage
        catalogueResponse={catalogueResponse}
        canonicalLink={canonicalLink}
        key={catalogueResponse.productDetails.urlSegment}
      />
    )
  }

  log.info(`Could not find template for entityType='${entityType}'`)
  return <ErrorPage />
}

export const getServerSideProps = withSessionSsr(async (context: GetServerSidePropsContext) => {
  const { lang, part1, part2 } = context.query

  if (typeof lang !== 'string' || typeof part1 !== 'string' || typeof part2 !== 'string') {
    return { error: true }
  }

  if (!isLanguageValid(lang)) {
    return { notFound: true }
  }

  const host = context.req.headers.host
  const agentConfiguration = getAgentConfiguration(host!)
  const catalogueResponse = await getCataloguePart2Response(agentConfiguration, part1, part2)
  const canonicalLink = createCanonicalLink(`${part1}/${part2}`)

  if (catalogueResponse?.status === 404) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      catalogueResponse: catalogueResponse,
      canonicalLink: canonicalLink,
    },
  }
})
